import { useEffect, useRef, useState } from "react";
import { api, ApiError, type AvatarSummary } from "./api.ts";
import { TOOL_LABEL } from "./toolLabels.ts";
import { Button } from "@/components/ui/button.tsx";
import { Alert, AlertDescription } from "@/components/ui/alert.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";

export type Template = {
  slug: string;
  name: string;
  voice: string;
  picture?: string;
  jobTitle: string;
  tagline: string;
  jobDescription: string;
  capabilities: string[];
  resources: { kind: string; label: string }[];
};

/**
 * Hire a predefined avatar.
 *
 * The capability chips are not decoration: a template without web access
 * genuinely cannot look anything up, and its briefing says so. Showing them on
 * the card is what makes that difference visible before hiring rather than
 * discovered mid-conversation.
 */
export function Marketplace({
  onClose,
  onHired,
}: {
  onClose: () => void;
  onHired: (id: string) => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Marketplace</DialogTitle>
          <DialogDescription>
            Find an employee for the work you have in mind.
          </DialogDescription>
        </DialogHeader>
        <MarketplaceSurface active onHired={onHired} />
      </DialogContent>
    </Dialog>
  );
}

export function MarketplaceSurface({
  active,
  onHired,
  suggestion,
}: {
  active: boolean;
  onHired: (id: string) => void;
  suggestion?: { query?: string; templateSlug?: string; version: number };
}) {
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [hired, setHired] = useState<string | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const pending = useRef(false);
  useEffect(() => {
    if (!suggestion) return;
    setQuery(suggestion.query ?? "");
    setSelected(suggestion.templateSlug ?? null);
  }, [suggestion]);

  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    setTemplates(null);
    const timer = setTimeout(
      () => {
        api<{ templates: Template[] }>(
          `/api/marketplace?q=${encodeURIComponent(query)}`,
          { signal: controller.signal },
        )
          .then((data) => {
            if (!controller.signal.aborted) setTemplates(data.templates);
          })
          .catch(() => {
            if (!controller.signal.aborted)
              setError("Could not load the marketplace. Try another search.");
          });
      },
      query ? 180 : 0,
    );
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [active, query]);

  const hire = async (t: Template) => {
    if (pending.current || uncertain) return;
    pending.current = true;
    setBusy(t.slug);
    setError(null);
    try {
      const { avatar } = await api<{ avatar: AvatarSummary }>("/api/avatars", {
        body: { templateSlug: t.slug },
      });
      setHired(t.name);
      onHired(avatar.id);
    } catch (e) {
      const unknown = !(e instanceof ApiError) || e.status >= 500;
      setUncertain(unknown);
      setError(
        unknown
          ? "The hiring result could not be confirmed. Check Employees in Settings to see whether it completed. Refresh the page after checking before starting another hire."
          : e.message,
      );
    } finally {
      pending.current = false;
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Choose an employee together. Delegate can search and select; review the
        details and choose Hire yourself.
      </p>
      {hired && (
        <p role="status" className="text-sm">
          {hired} has joined your employees. Open Settings when you're ready to
          talk to them.
        </p>
      )}
      <div className="grid gap-2">
        <Label htmlFor="marketplace-search">Find an employee</Label>
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id="marketplace-search"
            className="pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or job..."
          />
        </div>
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {!templates && !error && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading employees...
        </p>
      )}
      {templates?.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {query
            ? `Nothing matches "${query}".`
            : "No employees are available in the marketplace yet."}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {templates?.map((t) => (
          <Card
            key={t.slug}
            className={selected === t.slug ? "ring-2 ring-ring" : undefined}
          >
            {selected === t.slug && (
              <p className="px-6 pt-4 text-xs font-medium">
                Selected for your review
              </p>
            )}
            <CardHeader>
              <div className="flex items-start gap-3">
                {t.picture ? (
                  <img
                    src={t.picture}
                    alt=""
                    className="size-10 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                    {t.name.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <CardTitle>{t.name}</CardTitle>
                  <CardDescription>{t.jobTitle}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col gap-4">
              <p className="text-sm text-pretty text-muted-foreground">
                {t.tagline}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {t.capabilities.map((c) => (
                  <Badge key={c} variant="outline">
                    {TOOL_LABEL[c] ?? c}
                  </Badge>
                ))}
                {t.resources.map((r) => (
                  <Badge key={r.kind} variant="secondary">
                    {r.label}
                  </Badge>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                {t.jobDescription}
              </p>
              {t.resources.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  Hiring can start setup of the listed tools. Any applicable
                  infrastructure usage is separate from this listing.
                </p>
              )}
              <Button
                className="mt-auto w-full"
                onClick={() => void hire(t)}
                disabled={busy !== null || uncertain}
              >
                {busy === t.slug ? "Hiring..." : `Hire ${t.name}`}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
